import { UNGROUPED_RESOURCES_GROUP_ID } from '@/console/features/groups/components/group-list/group-list.constants';
import { useGroupedResourceCountsContext } from '@/console/features/groups/hooks/use-grouped-resource-counts/grouped-resource-counts.context';
import {
  useReadGroupedAssetsCountQuery,
  useReadUngroupedAssetsCountQuery,
} from '@/shared/redux/api/assets/groups/groups-api';
import {
  useReadGroupedIdentitiesCountQuery,
  useReadUngroupedIdentitiesCountQuery,
} from '@/shared/redux/api/ident/ident-api';
import {
  useReadGroupedIntegrationsCountQuery,
  useReadUngroupedIntegrationsCountQuery,
} from '@/shared/redux/api/integration/integrations-api';
import {
  useReadGroupedPipelinesCountQuery,
  useReadUngroupedPipelinesCountQuery,
} from '@/shared/redux/api/jobs/jobs-api';

export const useGroupResourcesCount = (groupId?: string) => {
  const isUngrouped = groupId === UNGROUPED_RESOURCES_GROUP_ID;

  const batched = useGroupedResourceCountsContext();
  const useBatched = batched.isEnabled && !!groupId && !isUngrouped;
  const batchedCounts = useBatched ? batched.getCounts(groupId!) : undefined;

  const {
    data: groupedAssetsData,
    isLoading: groupedAssetsLoading,
    isFetching: groupedAssetsFetching,
  } = useReadGroupedAssetsCountQuery(
    { groupId: groupId! },
    { skip: !groupId || isUngrouped || useBatched },
  );

  const {
    data: groupedIdentitiesData,
    isLoading: groupedIdentitiesLoading,
    isFetching: groupedIdentitiesFetching,
  } = useReadGroupedIdentitiesCountQuery(
    { groupId: groupId! },
    { skip: !groupId || isUngrouped || useBatched },
  );

  const {
    data: groupedPipelinesData,
    isLoading: groupedPipelinesLoading,
    isFetching: groupedPipelinesFetching,
  } = useReadGroupedPipelinesCountQuery(
    { groupId: groupId! },
    { skip: !groupId || isUngrouped || useBatched },
  );

  const {
    data: groupedIntegrationsData,
    isLoading: groupedIntegrationsLoading,
    isFetching: groupedIntegrationsFetching,
  } = useReadGroupedIntegrationsCountQuery(
    { groupId: groupId! },
    { skip: !groupId || isUngrouped || useBatched },
  );

  const groupedLoading = useBatched
    ? batched.isLoading
    : groupedAssetsLoading ||
      groupedIdentitiesLoading ||
      groupedPipelinesLoading ||
      groupedIntegrationsLoading;

  const groupedFetching = useBatched
    ? batched.isFetching
    : groupedAssetsFetching ||
      groupedIdentitiesFetching ||
      groupedPipelinesFetching ||
      groupedIntegrationsFetching;

  // One normalized shape for both data sources so the per-count reads below don't each
  // repeat the batched/non-batched branch.
  const groupedCounts = useBatched
    ? {
        subGroups: batchedCounts?.subGroups ?? 0,
        devices: batchedCounts?.devices ?? 0,
        projects: batchedCounts?.projects ?? 0,
        gateways: batchedCounts?.gateways ?? 0,
        documents: batchedCounts?.documents ?? 0,
        vaults: batchedCounts?.vaults ?? 0,
        licenses: batchedCounts?.licenses ?? 0,
        pipelines: batchedCounts?.pipelines ?? 0,
        users: batchedCounts?.users ?? 0,
        userRoles: batchedCounts?.userRoles ?? 0,
        integrations: batchedCounts?.integrations ?? 0,
      }
    : {
        subGroups:
          groupedAssetsData?.readSubGroupsForResourceGroup.itemCount || 0,
        devices: groupedAssetsData?.readAllDevices.itemCount || 0,
        projects: groupedAssetsData?.readAllProjects.itemCount || 0,
        gateways: groupedAssetsData?.readAllGateways.itemCount || 0,
        documents: groupedAssetsData?.readAllDocuments.itemCount || 0,
        vaults: groupedAssetsData?.readAllVaults.itemCount || 0,
        licenses: groupedAssetsData?.readAllLicenses.itemCount || 0,
        pipelines:
          groupedPipelinesData?.readAllPipelineConfigurations.itemCount || 0,
        users: groupedIdentitiesData?.readAllUsers.itemCount || 0,
        userRoles: groupedIdentitiesData?.readAllUserRoles.itemCount || 0,
        integrations:
          groupedIntegrationsData?.readAllIntegrations.itemCount || 0,
      };

  const groupedAllResourcesCount =
    groupedCounts.subGroups +
    groupedCounts.devices +
    groupedCounts.projects +
    groupedCounts.gateways +
    groupedCounts.documents +
    groupedCounts.pipelines +
    groupedCounts.vaults +
    groupedCounts.users +
    groupedCounts.userRoles +
    groupedCounts.licenses +
    groupedCounts.integrations;

  const {
    data: ungroupedAssetsData,
    isLoading: ungroupedAssetsLoading,
    isFetching: ungroupedAssetsFetching,
  } = useReadUngroupedAssetsCountQuery(undefined, {
    skip: !groupId || !isUngrouped,
  });

  const {
    data: ungroupedIdentitiesData,
    isLoading: ungroupedIdentitiesLoading,
    isFetching: ungroupedIdentitiesFetching,
  } = useReadUngroupedIdentitiesCountQuery(undefined, {
    skip: !groupId || !isUngrouped,
  });

  const {
    data: ungroupedPipelinesData,
    isLoading: ungroupedPipelinesLoading,
    isFetching: ungroupedPipelinesFetching,
  } = useReadUngroupedPipelinesCountQuery(undefined, {
    skip: !groupId || !isUngrouped,
  });

  const {
    data: ungroupedIntegrationsData,
    isLoading: ungroupedIntegrationsLoading,
    isFetching: ungroupedIntegrationsFetching,
  } = useReadUngroupedIntegrationsCountQuery(undefined, {
    skip: !groupId || !isUngrouped,
  });

  const ungroupedLoading =
    ungroupedAssetsLoading ||
    ungroupedIdentitiesLoading ||
    ungroupedPipelinesLoading ||
    ungroupedIntegrationsLoading;

  const ungroupedFetching =
    ungroupedAssetsFetching ||
    ungroupedIdentitiesFetching ||
    ungroupedPipelinesFetching ||
    ungroupedIntegrationsFetching;

  const ungroupedDevicesCount =
    ungroupedAssetsData?.readAllDevices.itemCount || 0;
  const ungroupedProjectsCount =
    ungroupedAssetsData?.readAllProjects.itemCount || 0;
  const ungroupedGatewaysCount =
    ungroupedAssetsData?.readAllGateways.itemCount || 0;
  const ungroupedDocumentsCount =
    ungroupedAssetsData?.readAllDocuments.itemCount || 0;
  const ungroupedVaultsCount =
    ungroupedAssetsData?.readAllVaults.itemCount || 0;
  const ungroupedLicensesCount =
    ungroupedAssetsData?.readAllLicenses.itemCount || 0;
  const ungroupedPipelinesCount =
    ungroupedPipelinesData?.readAllPipelineConfigurations.itemCount || 0;
  const ungroupedUsersCount =
    ungroupedIdentitiesData?.readAllUsers.itemCount || 0;
  const ungroupedRolesCount =
    ungroupedIdentitiesData?.readAllUserRoles.itemCount || 0;
  const ungroupedIntegrationsCount =
    ungroupedIntegrationsData?.readAllIntegrations.itemCount || 0;

  const ungroupedAllResourcesCount =
    ungroupedDevicesCount +
    ungroupedProjectsCount +
    ungroupedGatewaysCount +
    ungroupedDocumentsCount +
    ungroupedPipelinesCount +
    ungroupedVaultsCount +
    ungroupedUsersCount +
    ungroupedRolesCount +
    ungroupedLicensesCount +
    ungroupedIntegrationsCount;

  return {
    isLoading: isUngrouped ? ungroupedLoading : groupedLoading,
    isFetching: isUngrouped ? ungroupedFetching : groupedFetching,
    subGroupsCount: isUngrouped ? 0 : groupedCounts.subGroups,
    devicesCount: isUngrouped ? ungroupedDevicesCount : groupedCounts.devices,
    projectsCount: isUngrouped
      ? ungroupedProjectsCount
      : groupedCounts.projects,
    gatewaysCount: isUngrouped
      ? ungroupedGatewaysCount
      : groupedCounts.gateways,
    documentsCount: isUngrouped
      ? ungroupedDocumentsCount
      : groupedCounts.documents,
    pipelinesCount: isUngrouped
      ? ungroupedPipelinesCount
      : groupedCounts.pipelines,
    vaultsCount: isUngrouped ? ungroupedVaultsCount : groupedCounts.vaults,
    usersCount: isUngrouped ? ungroupedUsersCount : groupedCounts.users,
    rolesCount: isUngrouped ? ungroupedRolesCount : groupedCounts.userRoles,
    licensesCount: isUngrouped
      ? ungroupedLicensesCount
      : groupedCounts.licenses,
    integrationsCount: isUngrouped
      ? ungroupedIntegrationsCount
      : groupedCounts.integrations,
    allResourcesCount: isUngrouped
      ? ungroupedAllResourcesCount
      : groupedAllResourcesCount,
  };
};
